import {
  DropdownItem,
  OrderFilterOptions,
} from "src/Interfaces/dropdownInterfaces";
import { ISearchParameters, Report } from "../Interfaces/TableInterfaces";
import { Packet } from "../Interfaces/commonDtos";
import {
  IManualActionsTableReport,
  IManualActionsTableSearchParam,
  IFundOrderTableReport,
  IFundOrderTableSearchParams,
  IManualAddOrderFormValues,
  IEditManualOrderFormValues,
  IEditManualStepOrderResult,
  IPreviewEditVolumeRequest,
  IPreviewEditVolumeResult,
  IManualOrderPreview,
  IManualOrderTableReport,
  IManualSahraOrderTableReport,
  IManualSahraOrderTableSearchParam,
  IManualSubOrdersTableReport,
  IManualTablesSearchParam,
  IManualTradeTableReport,
} from "../Interfaces/manualInterfaces";
import {
  ErrorTableReport,
  FileOrderTableReport,
  IFailedOrderBulkPreviewResponse,
  IFailedOrderResendResult,
  IFailedOrderTableReport,
  ManualOrderRow,
} from "../Interfaces/orderInterfaces";
import { IOrderFileUploadResponse } from "../Interfaces/stepInterfaces";
import {
  deleteRequest,
  getRequest,
  getTable,
  postDownloadExcel,
  postRequest,
  putRequest,
} from "./requestTemplates";
import { AxiosRequestConfig } from "axios";

export const addManualBulkOrder = (
  orderRows: Array<ManualOrderRow> | null,
): Promise<Report<ErrorTableReport>> =>
  postRequest("/v1/manual/addBulkOrder", orderRows);

export const addManualShekarOrder = (
  formValues: IManualAddOrderFormValues,
): Promise<Packet<boolean>> => postRequest("/v1/manual/addOrder", formValues);

export const getManualOrderFilterOptions = (
  searchParam: IManualTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/manual/orderFilterOptions", searchParam);

export const editManualOrder = (
  formValues: IEditManualOrderFormValues,
): Promise<IEditManualStepOrderResult> =>
  putRequest("/v1/manual/editOrder", formValues);

export const previewEditManualOrderVolume = (
  body: IPreviewEditVolumeRequest,
): Promise<IPreviewEditVolumeResult> =>
  postRequest("/v1/manual/previewEditVolume", body);

export const getSimilarManualOrders = (
  manualStepOrderId: number,
): Promise<DropdownItem[]> =>
  getRequest(
    `/v1/manual/getSimilarOrders?manualStepOrderId=${manualStepOrderId}`,
  );

export const getManualOrderTable = (
  searchParam: IManualTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IManualOrderTableReport>> =>
  getTable<IManualTablesSearchParam, IManualOrderTableReport>(
    "/v1/manual/orderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getManualSahraOrderTable = (
  searchParam: IManualSahraOrderTableSearchParam,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IManualSahraOrderTableReport>> =>
  getTable<IManualSahraOrderTableSearchParam, IManualSahraOrderTableReport>(
    "/v1/manual/sahraOrderTable",
    searchParam,
    offset,
    pageSize,
  );

export const cancelManualOrder = (orderId: number): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/manual/cancel/${orderId}`);

export const getManualSubOrders = (
  searchParam: { manualStepOrderId: number },
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IManualSubOrdersTableReport>> =>
  getTable<{ manualStepOrderId: number }, IManualSubOrdersTableReport>(
    "/v1/manual/getSuborders",
    searchParam,
    offset,
    pageSize,
  );

export const getManualTradeFilterOptions = (
  searchParam: IManualTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/manual/tradeFilterOptions", searchParam);

export const getManualTradeTable = (
  searchParam: IManualTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IManualTradeTableReport>> =>
  getTable<IManualTablesSearchParam, IManualTradeTableReport>(
    "/v1/manual/tradetable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getManualErrorsTable = (
  searchParam: IManualTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<ErrorTableReport>> =>
  getTable<IManualTablesSearchParam, ErrorTableReport>(
    "/v1/manual/errortable",
    searchParam,
    offset,
    pageSize,
  );

export const getManualErrorFilterOptions = (
  searchParam: IManualTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/manual/errorFilterOptions", searchParam);

export const getManualFailedOrderFilterOptions = (
  searchParam: IManualTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/manual/failedOrderFilterOptions", searchParam);

export const getManualFailedOrderTable = <T extends ISearchParameters>(
  searchParam: T,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IFailedOrderTableReport>> =>
  getTable<T, IFailedOrderTableReport>(
    "/v1/manual/failedOrderTable",
    searchParam,
    offset,
    pageSize,
  );

export const previewManualFailedOrderResend = (
  id: number,
): Promise<IManualOrderPreview> =>
  postRequest("/v1/manual/previewFailedOrderResend", { id });

export const previewManualFailedOrderResendBulk = (
  ids: number[],
): Promise<IFailedOrderBulkPreviewResponse<IManualOrderPreview>> =>
  postRequest("/v1/manual/previewFailedOrderResendBulk", { ids });

export const resendManualFailedOrdersBulk = (
  items: Array<{ id: number; orderDto: any }>,
): Promise<IFailedOrderResendResult[]> =>
  postRequest("/v1/manual/resendFailedOrdersBulk", items);

export const markManualFailedOrderResolved = (
  id: number,
): Promise<Packet<boolean>> =>
  postRequest("/v1/manual/markFailedOrderResolved", { id });

export const sendManualFailedOrderResend = (
  orderDto: any,
): Promise<Packet<boolean>> => addManualShekarOrder(orderDto);

export const manualBulkOrderCancel = (
  rowIds: number[],
): Promise<Packet<boolean>> => deleteRequest("/v1/manual/bulkcancel", rowIds);

export const uploadManualOrderFile = (
  searchParam: FormData | null,
): Promise<IOrderFileUploadResponse<FileOrderTableReport>> =>
  postRequest<FormData | null, IOrderFileUploadResponse<FileOrderTableReport>>(
    "/v1/manual/uploadOrderFile",
    searchParam,
  );

// Unwrapped Report<T> shape for the generic multi-type UploadAll flow, which has no concept
// of the per-direction/symbol volume summary shown in the Manual page's own bulk-upload preview.
export const uploadManualOrderFileReport = (
  searchParam: FormData | null,
): Promise<Report<FileOrderTableReport>> =>
  uploadManualOrderFile(searchParam).then((resp) => resp.report);

export const getManualActionsTable = (
  searchParam: IManualActionsTableSearchParam,
): Promise<Report<IManualActionsTableReport>> =>
  getTable<IManualActionsTableSearchParam, IManualActionsTableReport>(
    "/v1/manual/actionTable",
    searchParam,
    0,
    50,
  );

export const cancelSahraOrder = (
  sahraOrderId: number,
): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/manual/cancelSahraOrder/${sahraOrderId}`);

export const getManualTradeExcelFile = (
  searchParam: IManualTablesSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(`/v1/manual/tradeExcel?${rand}`, searchParam);
};

export const getManualTradeAggregateExcelFile = (
  searchParam: IManualTablesSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/manual/tradeAggregateExcel?${rand}`,
    searchParam,
  );
};

export const getPreview = (
  formValues: IManualAddOrderFormValues,
): Promise<IManualOrderPreview> =>
  postRequest("/v1/manual/previewManualOrder", formValues);

export const getManualFundOrderFilterOptions = (
  searchParam: IFundOrderTableSearchParams,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/manual/fundOrderFilterOptions", searchParam);

export const getManualFundOrderTable = (
  searchParam: IFundOrderTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IFundOrderTableReport>> =>
  getTable<IFundOrderTableSearchParams, IFundOrderTableReport>(
    "/v1/manual/fundOrderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );
